import Layout from "../../Components/Layout/Layout";
import styles from "./ForgotPassword.module.scss";
import { Steps, Input, Button, message } from "antd";
import { useContext, useState } from "react";
import lock from "./images/lock.svg";
import Fireworks from "./images/Fireworks.svg";
import axios from "../../config/axios";
import { AuthModalContext } from "../../Contexts/AuthModalContext";

// Three backend calls, one per step:
//   /users/forgot-password    -> mails a 6 digit code
//   /users/verify-reset-code  -> checks the code, returns a short lived reset token
//   /users/reset-password     -> trades that token for the new password
const ForgotPassword = () => {
  // all states
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [userOTP, setUserOTP] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetToken, setResetToken] = useState("");

  return (
    <Layout title="Reset password">
      <div className={styles.forgot_password}>
        <div className={styles.step_wrapper}>
          <Steps size="small" current={step} responsive>
            <Steps.Step title="Email" />
            <Steps.Step title="OTP" />
            <Steps.Step title="Password" />
            <Steps.Step title="Success" />
          </Steps>
        </div>

        {step === 0 && (
          <EmailForm setStep={setStep} email={email} setEmail={setEmail} />
        )}

        {step === 1 && (
          <OTPForm
            setStep={setStep}
            email={email}
            userOTP={userOTP}
            setUserOTP={setUserOTP}
            setResetToken={setResetToken}
          />
        )}

        {step === 2 && (
          <NewPasswordForm
            setStep={setStep}
            password={password}
            setPassword={setPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
            resetToken={resetToken}
          />
        )}

        {step === 3 && <PasswordResetSuccess />}
      </div>
    </Layout>
  );
};

export default ForgotPassword;

// sub components
const EmailForm = ({ setStep, email, setEmail }) => {
  const [loading, setLoading] = useState(false);

  const sendOTP = async () => {
    if (!email) {
      message.error("Please enter email");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post("/users/forgot-password", { email });
      message.success(data.msg || `We sent a verification code to ${email}.`);
      setStep(1);
    } catch (error) {
      message.error(
        error.response?.data?.msg ||
          "We could not send the code right now. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.form_title_wrapper}>
        <img src={lock} alt="lock" />
        <h2>Reset your password</h2>
      </div>
      <div className={styles.form_desc}>
        If you have forgotten your password enter your registered email address
        and we send you a verification code then you can reset your password.
      </div>
      <div className={styles.main_form}>
        <div className={styles.form_input}>
          <label htmlFor="email">Email Address</label>
          <Input
            type="email"
            placeholder="yourname@yourdomain.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onPressEnter={sendOTP}
          />
        </div>
        <div className={styles.form_submit}>
          <Button
            type="primary"
            htmlType="submit"
            onClick={sendOTP}
            loading={loading}
            disabled={loading}
          >
            Send Now
          </Button>
        </div>
      </div>
    </div>
  );
};

const OTPForm = ({ setStep, email, userOTP, setUserOTP, setResetToken }) => {
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const saveOTP = async () => {
    if (!userOTP) {
      message.error("Please input OTP");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post("/users/verify-reset-code", {
        email,
        code: userOTP,
      });
      setResetToken(data.reset_token);
      setStep(2);
    } catch (error) {
      message.error(
        error.response?.data?.msg || "Could not verify this code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    try {
      setResending(true);
      const { data } = await axios.post("/users/forgot-password", { email });
      setUserOTP("");
      message.success(data.msg || "We sent you a new code.");
    } catch (error) {
      message.error(
        error.response?.data?.msg || "Could not send a new code. Please try again."
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.form_title_wrapper}>
        <h2>Verification Code</h2>
      </div>
      <div className={styles.form_desc}>
        Check your email address {email} and enter the verification code that we
        sent to you.
      </div>
      <div className={styles.main_form}>
        <div className={styles.form_input}>
          <label htmlFor="otp">Enter 6-digit code</label>
          <Input
            type="number"
            placeholder="123456"
            value={userOTP}
            maxLength={6}
            onChange={(e) => setUserOTP(e.target.value)}
            onPressEnter={saveOTP}
          />
        </div>
        <div className={styles.form_submit}>
          <Button
            type="primary"
            htmlType="submit"
            onClick={saveOTP}
            loading={loading}
            disabled={loading}
          >
            Confirm
          </Button>
        </div>
        <div className={styles.form_submit}>
          <Button type="link" onClick={resendOTP} loading={resending} disabled={resending}>
            Didn't get the code? Send it again
          </Button>
        </div>
      </div>
    </div>
  );
};

const NewPasswordForm = ({
  setStep,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  resetToken,
}) => {
  const [loading, setLoading] = useState(false);

  const resetPassword = async () => {
    if (!password) {
      message.error("Please enter new password.");
      return;
    }
    if (!confirmPassword) {
      message.error("Please confirm new password.");
      return;
    }
    if (password !== confirmPassword) {
      message.error("Password and confirmed password does not match.");
      return;
    }

    try {
      setLoading(true);
      await axios.post("/users/reset-password", {
        reset_token: resetToken,
        password,
      });
      setStep(3);
    } catch (error) {
      message.error(
        error.response?.data?.msg ||
          "Could not reset your password. Please start again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.form_title_wrapper}>
        <h2>Choose your new password</h2>
      </div>
      <div className={styles.main_form}>
        <div className={styles.form_input}>
          <label htmlFor="email">New Password</label>
          <Input.Password
            placeholder="8+ characters required"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <br />
        <div className={styles.form_input}>
          <label htmlFor="email">Confirm Password</label>
          <Input.Password
            placeholder="8+ characters required"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onPressEnter={resetPassword}
          />
        </div>
        <div className={styles.form_submit}>
          <Button
            type="primary"
            htmlType="submit"
            onClick={resetPassword}
            loading={loading}
            disabled={loading}
          >
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
};

const PasswordResetSuccess = () => {
  const { openSignIn } = useContext(AuthModalContext);

  return (
    <div className={styles.card}>
      <div className={styles.reset_success_wrapper}>
        <img className={styles.fireworks} src={Fireworks} alt="fireworks" />

        <h1>Congratulations!</h1>

        <div className={styles.animatedTick}>
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 130.2 130.2"
          >
            <circle
              className={`${styles.path} ${styles.circle}`}
              fill="none"
              stroke="#73AF55"
              strokeWidth="6"
              strokeMiterlimit="10"
              cx="65.1"
              cy="65.1"
              r="62.1"
            />
            <polyline
              className={`${styles.path} ${styles.check}`}
              fill="none"
              stroke="#73AF55"
              strokeWidth="6"
              strokeLinecap="round"
              strokeMiterlimit="10"
              points="100.2,40.2 51.5,88.8 29.8,67.5 "
            />
          </svg>
        </div>

        <h2>Your password has been changed.</h2>

        <div className={styles.signin_button}>
          <Button type="primary" onClick={() => openSignIn()}>
            Sign In
          </Button>
        </div>
      </div>
    </div>
  );
};
